<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'

    import { copyToClipboard, isNullOrWhitespace } from '@common/Common'

    import type { PanelistReadModel } from '@Evaluation/Estimations/Domain/readmodels'
    import type { SurveyAccessLinksViewModel } from '@Evaluation/Estimations/Components/SurveyAccessLinks'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: SurveyAccessLinksViewModel
    }>()

    const emit = defineEmits<{
        (e: 'send-email', payload: {
            SurveyId: number
            EmailSubject: string
            EmailBody: string
            Recipients: PanelistReadModel[] | null
        }): void
        (e: 'download-links', surveyId: number): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened)
    const initialized = computed(() => vm.value.intialized)
    const panelists = computed(() => vm.value.panelists)

    const step = computed<string>({
        get: () => vm.value.step,
        set: val => { vm.value.step = val }
    })

    // ---------------- local state ----------------

    const recipients = ref<PanelistReadModel[] | null>(null)
    const emailSubject = ref<string>('Please complete this survey')
    const emailBody = ref<string>('Please click on the following link to complete your survey: {url}')

    // ---------------- derived ----------------

    const emailBodyError = computed(() =>
        emailBody.value.indexOf('{url}') === -1
            ? '{url} is required in the message.'
            : null
    )

    const canSendEmail = computed(() =>
        isNullOrWhitespace(emailBodyError.value)
    )

    // ---------------- actions ----------------

    function handleSendEmail()
    {
        emit('send-email', {
            SurveyId: vm.value.surveyId,
            EmailSubject: emailSubject.value,
            EmailBody: emailBody.value,
            Recipients: recipients.value
        })
    }

    function handleDownloadLinks()
    {
        emit('download-links', vm.value.surveyId)
    }

    function showLinks()
    {
        step.value = 'links'
        recipients.value = null
    }

    function emailOne(rm: PanelistReadModel)
    {
        recipients.value = [rm]
        step.value = 'email'
    }

    function emailAll()
    {
        recipients.value = panelists.value
        step.value = 'email'
    }

    function copyAccessUrlToClipboard(rm: PanelistReadModel)
    {
        copyToClipboard(rm.AccessUrl)

        const url = rm.AccessUrl
        rm.AccessUrl = 'Copied to clipboard'

        const handle = setTimeout(() =>
        {
            rm.AccessUrl = url
            clearTimeout(handle)
        }, 500)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="!intialized">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <i class="fa fa-spinner fa-spin"></i> Loading...
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>

            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="intialized && step === 'links'">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div>
                            <table class="table table-hover">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Panelist</th>
                                        <th>Access link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="p in panelists" :key="p.ProjectUserId">
                                        <td>
                                            <span>{{p.Name}}</span><br />
                                            <span class="text-muted" v-if="p.HasEmail">{{p.Email}}</span>
                                            <span class="text-danger" v-if="!p.HasEmail">Email not provided</span>
                                        </td>
                                        <td class="align-middle">
                                            <div class="input-group">
                                                <input type="text" class="form-control" readonly v-model="p.AccessUrl" />
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary" type="button" title="Copy to clipboard" @click="copyAccessUrlToClipboard(p)">
                                                        <i class="fa fa-clipboard" aria-hidden="true"></i>
                                                    </button>
                                                    <button class="btn btn-outline-secondary" type="button" title="Send link via email" :disabled="!p.HasEmail" @click="emailOne(p)">
                                                        <i class="fa-regular fa-envelope"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="handleDownloadLinks" class="btn btn-outline-secondary">Download links</button>
                        <button type="button" @click.prevent="emailAll" class="btn btn-outline-secondary">Email all</button>
                    </div>
                </div>
            </div>

            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="intialized && step === 'email'">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Send email ({{recipients.length}})</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="survey-access-links-email-form" @submit.prevent="handleSendEmail">
                            <div class="form-group">
                                <label>Subject:</label>
                                <input type="text" class="form-control" v-model.trim="emailSubject" required />
                            </div>
                            <div class="form-group">
                                <textarea rows="5" class="form-control" v-model.trim="emailBody" required></textarea>
                                <div class="error">{{emailBodyError}}</div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="showLinks" class="btn btn-outline-secondary">Back</button>
                        <button type="submit" form="survey-access-links-email-form" :disabled="!canSendEmail" class="btn btn-primary">Send email</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
